/**
 * Integrytas Group - Base App (MOBILE)
 * Page: DOC.TEMPLATES — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/DocTemplateManagerPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + bottom-sheet detail + edit sheet)
 * over the SAME data/logic layer only:
 *   - reads via apiDb (doc_template + doc_template_tl), resolving the localized
 *     title/body against the ACTIVE locale (§8, §17.B4 — never a raw id/code reaches
 *     render; the template-kind and output-format codes are shown as translated
 *     labels via labelled constants, §17.B11),
 *   - the scalar edits (name / default output / active) route through the Common
 *     Routine via routedUpdate — §2 single write path; never a direct apiDb write.
 * It never re-renders the desktop grid; §16. Localized title/body authoring,
 * related bindings, versions, import/export and test-generate stay on the desktop —
 * the body is shown read-only here (mirrors MobileUserEditSheet's read-only roles).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, SquarePen, FileText, Table2 } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DOC.TEMPLATES';

type TemplateKind = 'MAILMERGE' | 'BANDED_REPORT';
type OutputFormat = 'HTML' | 'PDF' | 'XLSX' | 'CSV';

// §17.B11: CHECK-enum labels via labelled constants + t(), never bare literals.
const TEMPLATE_KINDS: { value: TemplateKind; labelKey: string; fallback: string }[] = [
  { value: 'MAILMERGE', labelKey: 'mobile.doc.kind.MAILMERGE', fallback: 'Mail Merge' },
  { value: 'BANDED_REPORT', labelKey: 'mobile.doc.kind.BANDED_REPORT', fallback: 'Banded Report' },
];

const OUTPUT_FORMATS: { value: OutputFormat; labelKey: string; fallback: string }[] = [
  { value: 'HTML', labelKey: 'mobile.doc.output.HTML', fallback: 'HTML' },
  { value: 'PDF', labelKey: 'mobile.doc.output.PDF', fallback: 'PDF' },
  { value: 'XLSX', labelKey: 'mobile.doc.output.XLSX', fallback: 'Excel (XLSX)' },
  { value: 'CSV', labelKey: 'mobile.doc.output.CSV', fallback: 'CSV' },
];

// Per-kind valid output formats (mirrors the desktop OUTPUT_BY_KIND / the
// doc_template_output_check DB rule).
const OUTPUT_BY_KIND: Record<TemplateKind, OutputFormat[]> = {
  MAILMERGE: ['HTML', 'PDF'],
  BANDED_REPORT: ['PDF', 'XLSX', 'CSV'],
};

function KindIcon({ kind }: { kind: TemplateKind }) {
  return kind === 'BANDED_REPORT' ? <Table2 /> : <FileText />;
}

interface MobileTemplateRow {
  doc_template_id: number;
  internal_code: string;
  name: string;
  template_kind: TemplateKind;
  primary_entity: string | null;
  pk_column: string | null;
  output_default: OutputFormat;
  is_active: 'Y' | 'N';
  title: string | null;
  body_html: string | null;
}

type StatusFacet = 'all' | 'active' | 'inactive';
type KindFacet = 'all' | TemplateKind;

interface TlRow { locale_code: string; title: string | null; body_html: string | null }
type RawTemplateRow = Omit<MobileTemplateRow, 'title' | 'body_html'> & {
  doc_template_tl?: TlRow[];
};

export function MobileDocTemplateManagerPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [rows, setRows] = useState<MobileTemplateRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [kindFacet, setKindFacet] = useState<KindFacet>('all');
  const [selected, setSelected] = useState<MobileTemplateRow | null>(null);
  const [editing, setEditing] = useState<MobileTemplateRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kindLabel = (k: TemplateKind): string => {
    const def = TEMPLATE_KINDS.find((x) => x.value === k);
    return def ? t(def.labelKey, def.fallback) : k;
  };
  const outputLabel = (o: OutputFormat): string => {
    const def = OUTPUT_FORMATS.find((x) => x.value === o);
    return def ? t(def.labelKey, def.fallback) : o;
  };

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('doc_template')
        .select('doc_template_id, internal_code, name, template_kind, primary_entity, pk_column, output_default, is_active, doc_template_tl ( locale_code, title, body_html )')
        .order('internal_code');
      if (e) throw e;

      const list: MobileTemplateRow[] = ((data ?? []) as RawTemplateRow[]).map((r) => {
        const tls = r.doc_template_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        const chosen = exact ?? fb;
        return {
          doc_template_id: r.doc_template_id,
          internal_code: r.internal_code,
          name: r.name,
          template_kind: r.template_kind,
          primary_entity: r.primary_entity,
          pk_column: r.pk_column,
          output_default: r.output_default,
          is_active: r.is_active,
          title: chosen?.title ?? null,
          body_html: chosen?.body_html ?? null,
        };
      });
      setRows(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (title/body follow it — §8).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (kindFacet !== 'all' && r.template_kind !== kindFacet) return false;
      if (!q) return true;
      return (
        r.internal_code.toLowerCase().includes(q) ||
        (r.name ?? '').toLowerCase().includes(q) ||
        (r.primary_entity ?? '').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet, kindFacet]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.doc.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const kindFacets: { key: KindFacet; label: string }[] = [
    { key: 'all', label: t('mobile.doc.kind_all', 'All kinds') },
    ...TEMPLATE_KINDS.map((k) => ({ key: k.value as KindFacet, label: t(k.labelKey, k.fallback) })),
  ];

  const toggleActive = async (r: MobileTemplateRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'doc_template', 'doc_template_id', r.doc_template_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.doc.title', 'Document Templates')}
        description={t('mobile.doc.description', 'Mail-merge and banded-report templates used to generate documents.')}
      />

      <MobileStatStrip
        stats={[
          { value: rows.length, label: t('mobile.doc.stat.total', 'Total') },
          { value: rows.filter((r) => r.is_active === 'Y').length, label: t('mobile.doc.stat.active', 'Active') },
          { value: rows.filter((r) => r.template_kind === 'MAILMERGE').length, label: t('mobile.doc.stat.merge', 'Merge') },
          { value: rows.filter((r) => r.template_kind === 'BANDED_REPORT').length, label: t('mobile.doc.stat.report', 'Reports') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.doc.search', 'Search templates')}
          aria-label={t('mobile.doc.search', 'Search templates')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <MobileFilterChips
        chips={kindFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => kindFacet === v}
        onSelect={(v) => setKindFacet(v as KindFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.doc.none', 'No templates match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.doc_template_id}
              lead={<KindIcon kind={r.template_kind} />}
              leadTone={r.template_kind === 'BANDED_REPORT' ? 'green' : 'blue'}
              title={r.name || r.internal_code}
              subtitle={`${r.internal_code} · ${kindLabel(r.template_kind)} · ${outputLabel(r.output_default)}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.doc.detail', 'Template detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><KindIcon kind={selected.template_kind} /></span>
              <span>
                <div className="m-nm">{selected.name || selected.internal_code}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.doc.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.doc.kind_lbl', 'Kind')}
                value={<MobileStatusPill tone="info">{kindLabel(selected.template_kind)}</MobileStatusPill>}
              />
              <MobileDetailRow
                label={t('mobile.doc.output_lbl', 'Default output')}
                value={<MobileStatusPill tone="info">{outputLabel(selected.output_default)}</MobileStatusPill>}
              />
              <MobileDetailRow
                label={t('mobile.doc.entity', 'Primary entity')}
                value={selected.primary_entity ?? t('common.none', 'None')}
              />
              <MobileDetailRow
                label={t('mobile.doc.pk', 'PK column')}
                value={selected.pk_column ?? t('common.none', 'None')}
              />
              <MobileDetailRow
                label={t('mobile.doc.doc_title', 'Title')}
                value={selected.title?.trim() ? selected.title : t('common.none', 'None')}
              />
            </MobileContentCard>

            {/* Body preview — read-only source; authoring stays on the desktop (§16). */}
            <MobileContentCard>
              <div className="m-formrow">
                <label>{t('mobile.doc.body', 'Body preview')}</label>
                <div className="m-formrow-ro" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '9rem', overflowY: 'auto', fontFamily: 'ui-monospace, monospace' }}>
                  {selected.body_html?.trim()
                    ? selected.body_html
                    : t('mobile.doc.no_body', 'No body for this locale.')}
                  <span className="m-formrow-hint"> · {t('mobile.doc.body_desktop', 'edit on desktop')}</span>
                </div>
              </div>
            </MobileContentCard>

            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.doc.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileDocTemplateEditSheet
        session={session}
        row={editing}
        kindLabel={kindLabel}
        outputLabel={outputLabel}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the master-table scalar fields (Name + Default output + Active),
// saved through the Common Routine (routedUpdate) — §2. The code and kind are
// immutable here, and the localized title/body live in doc_template_tl (authored
// on the desktop), so they are shown read-only (mirrors MobileUserEditSheet). The
// default-output options are constrained to those valid for the template's kind,
// mirroring the desktop OUTPUT_BY_KIND rule.
// ---------------------------------------------------------------------------

interface MobileDocTemplateEditSheetProps {
  session: AuthSession;
  row: MobileTemplateRow | null;
  kindLabel: (k: TemplateKind) => string;
  outputLabel: (o: OutputFormat) => string;
  onClose: () => void;
  onSaved: () => void;
}

function MobileDocTemplateEditSheet({ session, row, kindLabel, outputLabel, onClose, onSaved }: MobileDocTemplateEditSheetProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [output, setOutput] = useState<OutputFormat>('PDF');
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different template is opened.
  useEffect(() => {
    if (!row) return;
    setName(row.name ?? '');
    setOutput(row.output_default);
    setActive(row.is_active === 'Y');
    setError(null);
  }, [row]);

  const outputOpts = row ? OUTPUT_BY_KIND[row.template_kind] : OUTPUT_BY_KIND.MAILMERGE;

  const save = async () => {
    if (!row) return;
    if (!name.trim()) { setError(t('mobile.doc.name_required', 'Name is required.')); return; }
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'doc_template', 'doc_template_id', row.doc_template_id, {
      name: name.trim(),
      output_default: output,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.doc.edit_sheet', 'Edit template')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><KindIcon kind={row.template_kind} /></span>
              <span>
                <div className="m-nm">{t('mobile.doc.edit_title', 'Edit template')}</div>
                <div className="m-em">{row.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.doc.code', 'Code')}</label>
                <div className="m-formrow-ro">
                  {row.internal_code}
                  <span className="m-formrow-hint"> · {t('mobile.doc.code_immutable', 'cannot be changed')}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.doc.kind_lbl', 'Kind')}</label>
                <div className="m-formrow-ro">
                  {kindLabel(row.template_kind)}
                  <span className="m-formrow-hint"> · {t('mobile.doc.kind_desktop', 'set on desktop')}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-doc-name">{t('mobile.doc.name', 'Name')}</label>
                <input
                  id="m-doc-name"
                  className="m-inp"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-doc-output">{t('mobile.doc.output_lbl', 'Default output')}</label>
                <select id="m-doc-output" className="m-sel" value={output} onChange={(e) => setOutput(e.target.value as OutputFormat)}>
                  {outputOpts.map((o) => <option key={o} value={o}>{outputLabel(o)}</option>)}
                </select>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.doc.active_hint', 'Available for generation')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.doc.content', 'Title & body')}</label>
                <div className="m-formrow-ro">
                  {row.title?.trim() ? row.title : t('mobile.doc.no_title', 'No title for this locale')}
                  <span className="m-formrow-hint"> · {t('mobile.doc.body_desktop', 'edit on desktop')}</span>
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
